<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="spAccountsTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 class="modal-title" id="spAccountsTitle">Zugänge zum Partnerportal</h5>
            <div class="accounts-subtitle">{{ partnerName }}</div>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen"></button>
        </div>

        <div class="modal-body">
          <div v-if="errorMessage" class="alert alert-danger" role="alert">{{ errorMessage }}</div>

          <!-- Nach dem Anlegen bzw. Neusetzen: die Zugangsdaten stehen genau
               EINMAL im Klartext da. Danach ist das Passwort nicht mehr
               hervorholbar, deshalb bleibt dieser Block sichtbar, bis der
               Benutzer ausdrücklich „Fertig" wählt. -->
          <template v-if="view === 'credentials' && credentials">
            <div class="alert alert-success" role="alert">
              {{ credentials.isNew ? 'Zugang angelegt.' : 'Passwort neu gesetzt.' }}
            </div>

            <div class="credentials-box">
              <span class="credentials-label">Benutzername</span>
              <span class="credentials-value">{{ credentials.username }}</span>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="copy(credentials.username, 'user')">
                {{ copied === 'user' ? 'kopiert' : 'Kopieren' }}
              </button>

              <span class="credentials-label">Passwort</span>
              <span class="credentials-value">{{ credentials.password }}</span>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="copy(credentials.password, 'pass')">
                {{ copied === 'pass' ? 'kopiert' : 'Kopieren' }}
              </button>
            </div>

            <p class="credentials-hint">
              Bitte jetzt an den Partner weitergeben — das Passwort kann später
              <strong>nicht</strong> wieder angezeigt werden. Wer es verliert, bekommt ein neues.
            </p>
          </template>

          <!-- Formular: Anlegen oder Passwort neu setzen -->
          <template v-else-if="view === 'create' || view === 'password'">
            <div v-if="view === 'password' && passwordTarget" class="alert alert-warning" role="alert">
              Das Passwort von <strong>{{ passwordTarget.username }}</strong> wird ersetzt.
              Die bisherige Anmeldung des Partners gilt danach nicht mehr.
            </div>

            <div v-if="view === 'create'" class="row">
              <div class="col-md-6 mb-3">
                <label for="acc-username" class="form-label">Benutzername</label>
                <input
                  id="acc-username"
                  v-model="form.username"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': invalidFields.has('username') }"
                  autocomplete="off"
                  maxlength="100"
                  required
                />
                <div v-if="usernameTaken" class="invalid-feedback d-block">
                  Dieser Benutzername ist bereits vergeben.
                </div>
                <div v-else class="form-text">Kleinbuchstaben, Ziffern, Punkt, Bindestrich, Unterstrich.</div>
              </div>
              <div class="col-md-6 mb-3">
                <label for="acc-mail" class="form-label">Mail</label>
                <input
                  id="acc-mail"
                  v-model="form.mail"
                  type="email"
                  class="form-control"
                  :class="{ 'is-invalid': invalidFields.has('mail') }"
                  maxlength="64"
                />
                <div class="form-text">Optional.</div>
              </div>
            </div>

            <div class="mb-3">
              <label for="acc-password" class="form-label">Passwort</label>
              <div class="input-group">
                <input
                  id="acc-password"
                  v-model="form.password"
                  type="text"
                  class="form-control font-monospace"
                  :class="{ 'is-invalid': invalidFields.has('password') }"
                  autocomplete="off"
                  required
                />
                <button type="button" class="btn btn-outline-secondary" @click="rollPassword">Neu würfeln</button>
                <button type="button" class="btn btn-outline-secondary" @click="copy(form.password, 'form')">
                  {{ copied === 'form' ? 'kopiert' : 'Kopieren' }}
                </button>
              </div>
              <div class="form-text">
                Mindestens {{ PASSWORD_MIN_LENGTH }} Zeichen. Wird nach dem Speichern einmal angezeigt und danach nie wieder.
              </div>
            </div>
          </template>

          <!-- Liste -->
          <template v-else>
            <div v-if="loading" class="spinner" aria-label="Lädt"></div>

            <div v-else-if="accounts.length === 0" class="empty-state">
              Noch kein Zugang angelegt.
            </div>

            <div v-else class="table-responsive">
              <table class="table align-middle accounts-table">
                <thead>
                  <tr>
                    <th scope="col">Benutzername</th>
                    <th scope="col" style="width: 170px;">Letzte Anmeldung</th>
                    <th scope="col" style="width: 110px;">Zustand</th>
                    <th scope="col" class="text-end accounts-actions-col"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="account in accounts" :key="account.id" :class="{ 'account-disabled': !account.is_active }">
                    <td data-label="Benutzername">
                      <span class="account-username">{{ account.username }}</span>
                      <div v-if="account.mail" class="account-mail">{{ account.mail }}</div>
                    </td>
                    <td data-label="Letzte Anmeldung">{{ formatLastLogin(account.last_login) }}</td>
                    <td data-label="Zustand">
                      <span v-if="account.is_active" class="state-badge state-active">aktiv</span>
                      <span v-else class="state-badge state-inactive">deaktiviert</span>
                    </td>
                    <td class="text-end accounts-actions">
                      <!-- Durchstieg nur bei aktivem Zugang: fuer einen deaktivierten
                           wuerde der Link beim Einloesen sicher scheitern. -->
                      <template v-if="account.is_active">
                        <button
                          class="btn btn-sm btn-outline-primary me-1"
                          :disabled="busyId === account.id"
                          :title="'Als ' + account.username + ' im Partnerportal anmelden'"
                          @click="onImpersonate(account)"
                        >Anmelden als</button>
                        <button
                          class="btn btn-sm btn-outline-secondary me-1"
                          :disabled="busyId === account.id"
                          title="Einmal-Link kopieren, 5 Minuten gültig"
                          @click="onCopyImpersonationLink(account)"
                        >{{ copiedLinkId === account.id ? 'kopiert' : 'Link' }}</button>
                      </template>
                      <button
                        class="btn btn-sm btn-outline-secondary me-1"
                        :disabled="busyId === account.id"
                        @click="openPasswordForm(account)"
                      >Passwort neu setzen</button>
                      <button
                        v-if="account.is_active"
                        class="btn btn-sm btn-outline-danger"
                        :disabled="busyId === account.id"
                        @click="onDisable(account)"
                      >Deaktivieren</button>
                      <button
                        v-else
                        class="btn btn-sm btn-outline-success"
                        :disabled="busyId === account.id"
                        @click="onActivate(account)"
                      >Aktivieren</button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p class="accounts-note">
                „Anmelden als" öffnet das Partnerportal in einem neuen Fenster.
                „Link" legt stattdessen einen Einmal-Link in die Zwischenablage —
                er gilt <strong>einmal</strong> und nur 5 Minuten.
              </p>
            </div>
          </template>
        </div>

        <div class="modal-footer">
          <template v-if="view === 'credentials'">
            <button type="button" class="btn btn-primary" @click="backToList">Fertig</button>
          </template>

          <template v-else-if="view === 'create' || view === 'password'">
            <button type="button" class="btn btn-outline-secondary" :disabled="submitting" @click="backToList">
              Abbrechen
            </button>
            <button type="button" class="btn btn-primary" :disabled="submitting" @click="onSubmit">
              {{ submitting ? 'Speichert …' : (view === 'create' ? 'Zugang anlegen' : 'Passwort setzen') }}
            </button>
          </template>

          <template v-else>
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Schließen</button>
            <button type="button" class="btn btn-primary" :disabled="loading" @click="openCreateForm">
              Zugang anlegen
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import {
  ApiError,
  createAccount,
  createImpersonationLink,
  disableAccount,
  listAccounts,
  setAccountPassword,
  updateAccount,
  type ServicepartnerAccount,
} from '../services/servicepartner';

/**
 * Verwaltung der Portal-Zugänge eines Servicepartners.
 *
 * Das Passwort wird im Browser gewürfelt und VOR dem Speichern im Klartext
 * gezeigt — der Anleger muss es dem Partner durchgeben können. Der Server
 * speichert nur den Hash und gibt den Klartext nie zurück; deshalb ist dieser
 * Dialog die einzige Gelegenheit, ihn zu lesen.
 *
 * OpenSpec-Spec: r2-stammdaten-servicepartner
 */

const PASSWORD_MIN_LENGTH = 12;
const PASSWORD_LENGTH     = 16;
// Ohne verwechselbare Zeichen: kein I, l, 1, O, 0. Das Passwort wird am Telefon
// durchgegeben und von Hand abgetippt.
const PASSWORD_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';

interface FormState {
  username: string;
  mail:     string;
  password: string;
}

interface Credentials {
  username: string;
  password: string;
  isNew:    boolean;
}

type View = 'list' | 'create' | 'password' | 'credentials';

const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;

const servicepartnerId = ref<number | null>(null);
const partnerName      = ref('');

const view          = ref<View>('list');
const accounts      = ref<ServicepartnerAccount[]>([]);
const loading       = ref(false);
const errorMessage  = ref<string | null>(null);
const busyId        = ref<number | null>(null);
const submitting    = ref(false);
const invalidFields = ref<Set<string>>(new Set());
const usernameTaken = ref(false);

const copied         = ref<'user' | 'pass' | 'form' | null>(null);
const copiedLinkId   = ref<number | null>(null);
const form           = ref<FormState>(emptyForm());
const credentials    = ref<Credentials | null>(null);
const passwordTarget = ref<ServicepartnerAccount | null>(null);

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modal?.dispose();
  modal = null;
});

function emptyForm(): FormState {
  return { username: '', mail: '', password: '' };
}

/** `startInCreate` für den Weg direkt nach dem Anlegen eines Servicepartners. */
async function open(id: number, name: string, startInCreate = false): Promise<void> {
  servicepartnerId.value = id;
  partnerName.value      = name;
  errorMessage.value     = null;
  accounts.value         = [];
  credentials.value      = null;
  passwordTarget.value   = null;
  view.value             = 'list';
  modal?.show();
  await reload();
  if (startInCreate) {
    openCreateForm();
  }
}

function onHidden(): void {
  // Der Klartext soll nicht im Speicher liegen bleiben, wenn der Dialog zu ist.
  copied.value      = null;
  credentials.value = null;
  form.value        = emptyForm();
  view.value        = 'list';
}

async function reload(): Promise<void> {
  if (servicepartnerId.value === null) return;
  loading.value      = true;
  errorMessage.value = null;
  try {
    accounts.value = await listAccounts(servicepartnerId.value);
  } catch (e) {
    errorMessage.value = errorText(e);
  } finally {
    loading.value = false;
  }
}

function rollPassword(): void {
  const bytes = new Uint32Array(PASSWORD_LENGTH);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < PASSWORD_LENGTH; i++) {
    out += PASSWORD_ALPHABET[bytes[i] % PASSWORD_ALPHABET.length];
  }
  form.value.password = out;
}

function openCreateForm(): void {
  form.value          = emptyForm();
  invalidFields.value = new Set();
  usernameTaken.value = false;
  errorMessage.value  = null;
  rollPassword();
  view.value = 'create';
}

function openPasswordForm(account: ServicepartnerAccount): void {
  passwordTarget.value = account;
  form.value           = emptyForm();
  invalidFields.value  = new Set();
  errorMessage.value   = null;
  rollPassword();
  view.value = 'password';
}

function backToList(): void {
  copied.value         = null;
  credentials.value    = null;
  passwordTarget.value = null;
  form.value           = emptyForm();
  invalidFields.value  = new Set();
  usernameTaken.value  = false;
  errorMessage.value   = null;
  view.value           = 'list';
}

async function onSubmit(): Promise<void> {
  if (servicepartnerId.value === null) return;

  invalidFields.value = new Set();
  usernameTaken.value = false;
  errorMessage.value  = null;

  if (form.value.password.length < PASSWORD_MIN_LENGTH) {
    invalidFields.value = new Set(['password']);
    errorMessage.value  = `Das Passwort braucht mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`;
    return;
  }

  if (view.value === 'create' && form.value.username.trim() === '') {
    invalidFields.value = new Set(['username']);
    errorMessage.value  = 'Bitte einen Benutzernamen vergeben.';
    return;
  }

  submitting.value = true;
  try {
    if (view.value === 'create') {
      const mail = form.value.mail.trim();
      const account = await createAccount(servicepartnerId.value, {
        username: form.value.username.trim().toLowerCase(),
        password: form.value.password,
        mail:     mail === '' ? null : mail,
      });
      credentials.value = { username: account.username, password: form.value.password, isNew: true };
    } else if (passwordTarget.value !== null) {
      const account = await setAccountPassword(
        servicepartnerId.value,
        passwordTarget.value.id,
        form.value.password,
      );
      credentials.value = { username: account.username, password: form.value.password, isNew: false };
    }
    view.value = 'credentials';
    await reload();
  } catch (e) {
    if (e instanceof ApiError && e.code === 'CONFLICT') {
      // Das gewürfelte Passwort bleibt stehen — nur der Name ist das Problem.
      usernameTaken.value = true;
      invalidFields.value = new Set(['username']);
    } else if (e instanceof ApiError && e.code === 'BAD_REQUEST' && e.fields) {
      invalidFields.value = new Set(e.fields);
      errorMessage.value  = 'Bitte Eingaben prüfen.';
    } else {
      errorMessage.value = errorText(e);
    }
  } finally {
    submitting.value = false;
  }
}

async function onDisable(account: ServicepartnerAccount): Promise<void> {
  if (servicepartnerId.value === null) return;
  const label = account.username;
  if (!confirm(`Zugang „${label}" deaktivieren? Der Partner kommt damit nicht mehr ins Portal. Später wieder aktivierbar.`)) {
    return;
  }
  await applyChange(account.id, () => disableAccount(servicepartnerId.value as number, account.id));
}

async function onActivate(account: ServicepartnerAccount): Promise<void> {
  if (servicepartnerId.value === null) return;
  await applyChange(account.id, () => updateAccount(servicepartnerId.value as number, account.id, { is_active: true }));
}

async function applyChange(userId: number, action: () => Promise<ServicepartnerAccount>): Promise<void> {
  busyId.value       = userId;
  errorMessage.value = null;
  try {
    const updated = await action();
    accounts.value = accounts.value.map(a => (a.id === updated.id ? updated : a));
  } catch (e) {
    errorMessage.value = errorText(e);
  } finally {
    busyId.value = null;
  }
}

/**
 * Sofort anmelden: das Fenster wird VOR dem Server-Aufruf geoeffnet, sonst
 * blockt der Popup-Schutz — nach einem await gilt der Klick nicht mehr als
 * Benutzergeste. Erst danach wird der Link nachgeladen und das Fenster
 * dorthin geschickt.
 */
async function onImpersonate(account: ServicepartnerAccount): Promise<void> {
  if (servicepartnerId.value === null) return;

  const tab = window.open('', '_blank');
  busyId.value       = account.id;
  errorMessage.value = null;
  try {
    const link = await createImpersonationLink(servicepartnerId.value, account.id);
    if (tab !== null) {
      tab.location.href = link.url;
    } else {
      // Popup geblockt: dann wenigstens den Link in die Zwischenablage.
      await navigator.clipboard.writeText(link.url);
      errorMessage.value = 'Das Fenster wurde blockiert. Der Link liegt in der Zwischenablage (5 Minuten gültig).';
    }
  } catch (e) {
    tab?.close();
    errorMessage.value = impersonationError(e);
  } finally {
    busyId.value = null;
  }
}

async function onCopyImpersonationLink(account: ServicepartnerAccount): Promise<void> {
  if (servicepartnerId.value === null) return;

  busyId.value       = account.id;
  errorMessage.value = null;
  try {
    const link = await createImpersonationLink(servicepartnerId.value, account.id);
    await navigator.clipboard.writeText(link.url);
    copiedLinkId.value = account.id;
    window.setTimeout(() => {
      if (copiedLinkId.value === account.id) copiedLinkId.value = null;
    }, 2000);
  } catch (e) {
    errorMessage.value = impersonationError(e);
  } finally {
    busyId.value = null;
  }
}

function impersonationError(e: unknown): string {
  if (e instanceof ApiError && e.code === 'CONFLICT') {
    if (e.message.includes('impersonation_disabled')) {
      return 'Der Durchstieg ist in dieser Umgebung nicht eingerichtet.';
    }
    return 'Für diesen Zugang ist keine Anmeldung möglich.';
  }
  return errorText(e);
}

async function copy(text: string, which: 'user' | 'pass' | 'form'): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    // Kurze Rückmeldung am Knopf — sonst weiß niemand, ob der Klick etwas tat.
    copied.value = which;
    window.setTimeout(() => {
      if (copied.value === which) copied.value = null;
    }, 1500);
  } catch (e) {
    // Ohne Clipboard-Recht (oder ohne HTTPS) bleibt der Wert lesbar auf dem
    // Schirm — kein Grund für eine Fehlermeldung.
    console.warn('Kopieren nicht möglich:', e);
  }
}

/**
 * `2026-09-02 14:32:11` ist Datenbankschreibweise, nicht Lesestoff.
 * Sekunden sagen hier nichts, also weg damit.
 */
function formatLastLogin(value: string | null): string {
  if (value === null || value === '') return 'noch nie';
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value);
  if (m === null) return value;
  const [, year, month, day, hour, minute] = m;
  return `${day}.${month}.${year}, ${hour}:${minute}`;
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error)    return e.message;
  return 'Unbekannter Fehler.';
}

defineExpose({ open });
</script>

<style scoped>
.accounts-subtitle {
  font-size: 13px;
  color: var(--muted, #6c757d);
  margin-top: 2px;
}

/* --- Liste ---------------------------------------------------------------- */

.accounts-table th {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--muted, #6c757d);
  border-bottom-width: 1px;
  white-space: nowrap;
}

/* Der Benutzername ist die Kennung der Zeile. Monospace ja — aber NICHT in
   Bootstraps <code>-Rosa: Rot liest sich als Fehler, und hier ist nichts kaputt. */
.account-username {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-size: 14px;
  color: #212529;
}

.account-mail {
  font-size: 13px;
  color: var(--muted, #6c757d);
  line-height: 1.3;
}

/* Die Aktionen bleiben in EINER Zeile. Vorher brach „Passwort neu setzen" um,
   die Knöpfe stapelten sich und liefen aus der Spalte heraus. */
.accounts-actions-col { width: 290px; }

.accounts-actions { white-space: nowrap; }

.accounts-actions .btn { white-space: nowrap; }

.state-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}

.state-active {
  background: rgba(25, 135, 84, 0.12);
  color: #146c43;
}

.state-inactive {
  background: rgba(0, 0, 0, 0.07);
  color: #5c636a;
}

/* Ein deaktivierter Zugang tritt zurück — aber das Kennzeichen bleibt lesbar,
   sonst sucht man den Zustand, den man gerade sucht. */
.account-disabled .account-username,
.account-disabled .account-mail {
  opacity: 0.55;
}

.accounts-note {
  margin: 4px 2px 0;
  font-size: 13px;
  color: var(--muted, #6c757d);
}

.empty-state {
  padding: 28px 0;
  text-align: center;
  color: var(--muted, #6c757d);
}

/* --- Zugangsdaten ------------------------------------------------------- */

/* Raster statt Flexzeilen: so bilden Werte und Kopieren-Knöpfe je eine Kante,
   auch wenn Benutzername und Passwort unterschiedlich lang sind. */
.credentials-box {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 18px 20px;
}

.credentials-label {
  font-size: 13px;
  color: var(--muted, #6c757d);
}

/* Wird am Telefon vorgelesen und abgetippt: groß, Monospace, mit Laufweite. */
.credentials-value {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-size: 19px;
  letter-spacing: 0.04em;
  padding: 6px 10px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 6px;
  color: #212529;
  user-select: all;
  justify-self: start;
}

.credentials-hint {
  margin-top: 16px;
  margin-bottom: 0;
  font-size: 14px;
}

@media (max-width: 576px) {
  .credentials-box {
    grid-template-columns: 1fr auto;
  }
  .credentials-label {
    grid-column: 1 / -1;
    margin-bottom: -6px;
  }
  .credentials-value { font-size: 17px; }
}
</style>
